/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import createLocalZustand from "@/shared/state/LocalZustand";
import type { DataGridColumns, DataGridRows, DataGridFetchSettings } from "@/shared/types";

export type DataGridProviderProps = {
  columns: DataGridColumns;
  rows: DataGridRows;
  getRowId: (row: object) => string;
  fetchSettings: Partial<DataGridFetchSettings>;
  isLoading: boolean;
};

export type DataGridContext = {
  setRows: (rows: DataGridRows, replace?: boolean) => void;
  setColumns: (columns: DataGridColumns, replace?: boolean) => void;

  selectedRows: Set<string>;
  setSelectedRow: (
    rowId: string,
    isSelected: boolean,
    replace?: boolean
  ) => void;
  selectAllRows: () => void;
  clearSelectedRows: () => void;
} & DataGridProviderProps;

export const {
  Context: DataGridContextInstance,
  Provider: DataGridProvider,
  useStore: useDataGridStore,
} = createLocalZustand<DataGridContext>((initialState) => (set, get) => ({
  // Dynamic store data
  columns: {},
  rows: [],
/**
 * getRowId function.
 *
 * @returns {*} Return value description
 */
  getRowId: () => "",

  selectedRows: new Set(),
/**
 * setSelectedRow function.
 *
 * @param {*} rowId - Parameter description
 * @param {*} newState - Parameter description
 * @param {*} replace - Parameter description
 *
 * @returns {*} Return value description
 */
  setSelectedRow: (rowId, newState, replace = false) => {
    set((state) => {
      const selectedRows = state.selectedRows;
      let clonedSelectedRows = new Set(selectedRows);
      newState === true && clonedSelectedRows.add(rowId);
      newState === false && clonedSelectedRows.delete(rowId);
      return { selectedRows: clonedSelectedRows };
    }, replace);
  },
/**
 * selectAllRows function.
 *
 * @returns {*} Return value description
 */
  selectAllRows: () =>
    set((state) => {
      const getRowId = state.getRowId;
      const rowIds = state.rows.map((row) => getRowId(row));
      return { selectedRows: new Set(rowIds) };
    }),
/**
 * clearSelectedRows function.
 *
 * @returns {*} Return value description
 */
  clearSelectedRows: () => set(() => ({ selectedRows: new Set() })),

  ...initialState,
  fetchSettings: {
    page: 1,
    pageSize: 10,
    ...(initialState?.fetchSettings ?? {}),
  },

  isLoading: true,

  // Static manipulators
/**
 * setColumns function.
 *
 * @param {*} columns - Parameter description
 * @param {*} replace - Parameter description
 */
  setColumns: (columns, replace = false) =>
    set((state) => {
      return { columns: { ...(!replace && state.columns), ...columns } };
    }),
/**
 * setRows function.
 *
 * @param {*} rows - Parameter description
 * @param {*} replace - Parameter description
 *
 * @returns {*} Return value description
 */
  setRows: (rows, replace = false) =>
    set((state) => {
      state.clearSelectedRows();
      return { rows: [...(!replace ? state.rows : []), ...rows] };
    }),
/**
 * setFetchSettings function.
 *
 * @param {*} fetchSettings - Parameter description
 *
 * @returns {*} Return value description
 */
  setFetchSettings: (fetchSettings: Partial<DataGridFetchSettings>) =>
    set((state) => {
      return { fetchSettings: { ...state.fetchSettings, ...fetchSettings } };
    }),
/**
 * setIsLoading function.
 *
 * @param {*} isLoading - Parameter description
 *
 * @returns {*} Return value description
 */
  setIsLoading: (isLoading: boolean) => set((state) => ({ isLoading })),
}));
